import dayjs from 'dayjs';
import { Klydo, KlydoReviewStatus, KlydoType } from '../../../api/klydo';
import {
  PublicationDestination,
  PublishModalVariant,
  PublishSelection,
} from './PublishModal';
import { PublishSuccess } from './PublishSuccessModal';

/**
 * The publication decision table for a read-only klydo, in one place.
 *
 * Both halves live here on purpose. Which publication action to offer and whether
 * the artist may go back to editing are answers to the same question — what state
 * is this klydo in — and when they were derived separately they drifted: a
 * rejected SEALED klydo was offered "Edit", which withdrew its request and opened
 * an editor where every autosave then failed.
 *
 * The model behind it:
 *   - `type: Sealed` means the artist can never edit it again. One-way ratchet,
 *     reached by an approved publication request OR by the klydo's collection
 *     being approved.
 *   - `public.reviewStatus` is the publication request, independent of the seal.
 *   - `public.status` is the serving gate: true = public right now. The requested
 *     go-live date lives on the request itself
 *     (`public.reviewStatus.requestPayload.timestamp`) and is never the gate.
 */

type PublicationKlydo =
  | Pick<Klydo, 'type' | 'public' | 'collectionId'>
  | Partial<Pick<Klydo, 'type' | 'public' | 'collectionId'>>
  | undefined;

const isSealed = (klydo: PublicationKlydo): boolean =>
  klydo?.type === KlydoType.Sealed;

/**
 * Which variant of the date picker to offer, or null for none.
 *
 * `SetPublicationDate` requires an APPROVED request on a klydo that is not public
 * yet — once the gate is open there is no future date left to move, so the picker
 * has nothing to do. Approval itself opens the gate immediately unless the
 * requested date is still in the future, so `public.status` is the only honest
 * test here; a stored date says nothing about whether it already fired.
 *
 * A sealed klydo with no approved request gets NOTHING. It used to be offered a
 * publication request of its own here; the design took that button off the sealed
 * page, so the only publication route left is the editor's "Done" CTA on a draft.
 */
export const getPublishModalVariant = (
  klydo: PublicationKlydo,
): PublishModalVariant | null => {
  // Already served: there is nothing to offer, whatever the review record says.
  // Approval opens the gate immediately unless the requested date is still in the
  // future, so this flag — not a stored date — is what says the klydo is live.
  if (klydo?.public?.status) {
    return null;
  }

  return klydo?.public?.reviewStatus?.state === KlydoReviewStatus.Approved
    ? PublishModalVariant.SetPublicationDate
    : null;
};

/**
 * Whether withdrawing this klydo's publication request drops the artist back
 * into the editor.
 *
 * Only a DRAFT does. A sealed klydo stays sealed after the withdrawal, so the
 * editor would be a dead one — `PUT /studio/klydos/:klydoId` runs on the
 * DraftKlydo model and rejects a sealed klydo, and the studio's upsert mutation
 * short-circuits on the cached seal while still applying its optimistic update,
 * so edits LOOK saved and vanish on the next refetch. A sealed klydo therefore
 * stays in the read-only view — which, since the submit button was taken off that
 * page, now leaves it with no publication action at all.
 */
export const returnsToEditorAfterWithdraw = (
  klydo: PublicationKlydo,
): boolean => !isSealed(klydo);

/**
 * Whether to offer "Edit", which withdraws the publication request and returns the
 * klydo to the editable view.
 *
 * Only a rejected DRAFT qualifies. A sealed klydo stays sealed after withdrawing,
 * so the editor would open and then every autosave would fail —
 * `PUT /studio/klydos/:klydoId` runs on the DraftKlydo model and rejects a sealed
 * klydo outright. Such a klydo used to get a publication request of its own on the
 * read-only page; that button is gone, so it now gets nothing.
 */
export const canReturnToEditing = (klydo: PublicationKlydo): boolean =>
  klydo?.public?.reviewStatus?.state === KlydoReviewStatus.Rejected &&
  !isSealed(klydo);

/**
 * What date the publish modal should open on, and which radio should be selected.
 *
 * Lives here rather than in either view because BOTH render the same modal and were
 * doing different things: the read-only view fell back to the sponsored calendar
 * event while the editable view — the primary submit path — passed no fallback and
 * no default selection at all.
 *
 * The stored value comes off the REQUEST (`requestPayload.timestamp`), not the
 * serving gate, because that is what the artist last asked for.
 */
export const getPublicationModalDefaults = (
  klydo: PublicationKlydo,
  calendarEvent?: { date: { year: number; month: number; day: number } } | null,
): { prefilledTimestamp: number | null; isScheduledForLater: boolean } => {
  const prefilledTimestamp =
    klydo?.public?.reviewStatus?.requestPayload?.timestamp ??
    (calendarEvent
      ? new Date(
          calendarEvent.date.year,
          calendarEvent.date.month - 1,
          calendarEvent.date.day,
        ).getTime()
      : null);

  return {
    prefilledTimestamp,
    isScheduledForLater:
      !!prefilledTimestamp && prefilledTimestamp > Date.now(),
  };
};

/**
 * The confirmation the artist sees after choosing a destination.
 *
 * Both call sites render the same modal, so the copy lives here rather than in
 * either of them. The Collection branch is the reason this exists at all: it
 * submits nothing, so there is no mutation whose success toast could say what
 * happened.
 */
export const getPublishSuccess = (
  selection: PublishSelection,
): PublishSuccess =>
  selection.destination === PublicationDestination.Collection
    ? {
        title: 'Saved for Collection',
        caption:
          'This Klydo will be saved as a draft and reviewed once you submit the Collection.',
      }
    : {
        title: 'Sent for review',
        caption: selection.isScheduled
          ? `Goes live on ${dayjs(selection.publicationTimestamp).format(
              'MMM D, YYYY',
            )} once approved.`
          : 'Goes live right after approval.',
      };

/**
 * What the editor's single CTA says, and whether it still does anything.
 *
 * The button is both the action and the status: "Done" asks where the klydo
 * should go, and once it has gone somewhere it names the destination and stops
 * being clickable. There is no third thing the artist can do to it from here.
 */
export enum SubmitCtaState {
  /** Nothing chosen yet — the button opens the destination dialog. */
  Ready = 'Ready',
  /** Sent to the Feed: a live publication request, or already serving. */
  SentToFeed = 'SentToFeed',
  /** Belongs to a collection, which is reviewed as a whole. */
  InCollection = 'InCollection',
}

export const SUBMIT_CTA_LABEL: Record<SubmitCtaState, string> = {
  [SubmitCtaState.Ready]: 'Done',
  [SubmitCtaState.SentToFeed]: 'Done - Feed',
  [SubmitCtaState.InCollection]: 'Done - Collection',
};

/**
 * Which of the three the klydo is in.
 *
 * A REJECTED request is deliberately not `SentToFeed`: the reviewer handed the
 * klydo back, so the button has to be live again — that is the whole point of
 * "Edit & resubmit".
 *
 * The feed wins over collection membership when a klydo is somehow both, because
 * an open publication request is the thing the artist is currently waiting on.
 */
export const getSubmitCtaState = (klydo: PublicationKlydo): SubmitCtaState => {
  const state = klydo?.public?.reviewStatus?.state;
  const sentToFeed =
    klydo?.public?.status ||
    state === KlydoReviewStatus.Pending ||
    state === KlydoReviewStatus.Approved;

  if (sentToFeed) return SubmitCtaState.SentToFeed;
  if (klydo?.collectionId) return SubmitCtaState.InCollection;
  return SubmitCtaState.Ready;
};
